'use client'

import { type Ref, type RefCallback, useCallback, useEffect, useRef } from 'react'

/**
 * `autoFocus` without the scroll. React's own `autoFocus` calls a plain `element.focus()` on
 * mount, and the browser then scrolls the page to bring the field into view — so an Order
 * Form row remounting (every linked tab switch re-selects its instrument) jumped a
 * scrolled-down desktop dashboard back to the top. This focuses with `preventScroll`, once,
 * if `enabled` was true when the element mounted (same semantics as `autoFocus`).
 *
 * Returns a ref callback; pass the caller's own ref through `forwardRef` to keep it working.
 */
export function useFocusOnMount<T extends HTMLElement>(
  enabled: boolean,
  forwardRef?: Ref<T>
): RefCallback<T> {
  const elementRef = useRef<T | null>(null)
  const enabledAtMountRef = useRef(enabled)

  useEffect(() => {
    if (enabledAtMountRef.current) elementRef.current?.focus({ preventScroll: true })
  }, [])

  return useCallback(
    (element: T | null) => {
      elementRef.current = element
      if (typeof forwardRef === 'function') forwardRef(element)
      else if (forwardRef) (forwardRef as { current: T | null }).current = element
    },
    [forwardRef]
  )
}
